/**
 * Founding Member API Service Layer
 *
 * All founding member operations go through Supabase Edge Functions.
 * This service uses the adapter pattern — components never call
 * Edge Functions directly.
 *
 * Auth: We call supabase.auth.getSession() before every request to
 * ensure the access token is refreshed. The Supabase client then
 * auto-attaches the fresh token to functions.invoke() calls — no
 * manual Authorization headers needed. This prevents stale-token 401s.
 */

import { supabase } from '@/lib/supabase';
import type {
  FoundingMemberStatus,
  FeedbackSubmission,
  FeedbackSubmissionResult,
  SeatReservationResult,
  BadgeUpgradeRequest,
} from '@/types/founding';

/**
 * Ensure the Supabase client has a fresh (non-expired) session.
 * getSession() auto-refreshes if the token is expired.
 * Throws if there is no active session at all.
 */
async function requireFreshSession(): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
}

// ─── Public API ─────────────────────────────────────────────────

/**
 * Reserve a founding member seat for the current user.
 * Called once after first sign-in / account creation.
 */
export async function reserveFoundingSeat(): Promise<SeatReservationResult> {
  await requireFreshSession();
  const { data, error } = await supabase.functions.invoke('reserve-founding-seat', {
    body: {},
  });
  if (error) throw new Error(error.message);
  return data;
}

/**
 * Get the full founding member status for the current user.
 * Called on app load to hydrate FoundingMemberContext.
 */
export async function getFoundingMemberStatus(): Promise<FoundingMemberStatus> {
  await requireFreshSession();
  const { data, error } = await supabase.functions.invoke('get-founding-member-status', {
    body: {},
  });
  if (error) throw new Error(error.message);
  return data;
}

/**
 * Submit founding member feedback.
 */
export async function submitFoundingFeedback(
  submission: FeedbackSubmission,
): Promise<FeedbackSubmissionResult> {
  await requireFreshSession();
  const { data, error } = await supabase.functions.invoke('submit-founding-feedback', {
    body: submission,
  });
  if (error) throw new Error(error.message);
  return data;
}

// ─── Admin API ──────────────────────────────────────────────────

/**
 * Upgrade a founding member's badge tier (admin only).
 */
export async function adminUpgradeBadge(
  request: BadgeUpgradeRequest,
): Promise<{ success: boolean; updatedTier: string }> {
  await requireFreshSession();
  const { data, error } = await supabase.functions.invoke('admin-upgrade-badge', {
    body: request,
  });
  if (error) throw new Error(error.message);
  return data;
}

/**
 * Get all founding members with their feedback (admin only).
 * Routes through edge function to bypass RLS using service role.
 */
export async function adminGetAllMembers(): Promise<{
  members: Record<string, unknown>[];
  feedback: Record<string, unknown>[];
  emails: Record<string, string>;
}> {
  await requireFreshSession();
  const { data, error } = await supabase.functions.invoke('admin-list-members', {
    body: {},
  });
  if (error) throw new Error(error.message);
  return data;
}

/**
 * Update feedback review status (admin only).
 * Routes through edge function to bypass RLS using service role.
 */
export async function adminUpdateFeedbackStatus(
  feedbackId: string,
  reviewStatus: 'pending' | 'reviewed' | 'actioned',
  reviewNote?: string,
): Promise<{ success: boolean }> {
  await requireFreshSession();
  const { data, error } = await supabase.functions.invoke('admin-update-feedback', {
    body: { feedbackId, reviewStatus, reviewNote },
  });
  if (error) throw new Error(error.message);
  return data;
}

// ─── Usage Tracking API ────────────────────────────────────────

export interface UsageSummary {
  byProvider: Record<
    string,
    {
      calls: number;
      inputTokens: number;
      outputTokens: number;
      totalTokens: number;
      characters: number;
      cost: number;
    }
  >;
  byFunction: Record<string, { calls: number; cost: number }>;
  totalCost: number;
  totalCalls: number;
  rows: number;
}

export interface UsageDaily {
  date: string;
  calls: number;
  cost: number;
}

export interface UsageRecentEntry {
  id: string;
  api_provider: string;
  edge_function: string;
  model: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  characters_used: number | null;
  estimated_cost_usd: number | null;
  created_at: string;
  success: boolean;
  error_message: string | null;
  response_time_ms: number | null;
}

/**
 * Get usage summary stats (admin only).
 */
export async function adminGetUsageSummary(days = 30): Promise<UsageSummary> {
  await requireFreshSession();
  const { data, error } = await supabase.functions.invoke('admin-get-usage', {
    body: { mode: 'summary', days },
  });
  if (error) throw new Error(error.message);
  return data.summary;
}

/**
 * Get daily usage breakdown (admin only).
 */
export async function adminGetUsageDaily(days = 30): Promise<UsageDaily[]> {
  await requireFreshSession();
  const { data, error } = await supabase.functions.invoke('admin-get-usage', {
    body: { mode: 'daily', days },
  });
  if (error) throw new Error(error.message);
  return data.daily;
}

/**
 * Get recent usage log entries (admin only).
 */
export async function adminGetUsageRecent(limit = 50): Promise<UsageRecentEntry[]> {
  await requireFreshSession();
  const { data, error } = await supabase.functions.invoke('admin-get-usage', {
    body: { mode: 'recent', limit },
  });
  if (error) throw new Error(error.message);
  return data.recent;
}

// ─── User Analytics API ─────────────────────────────────────────

export interface UserAnalytics {
  totalUsers: number;
  dau: number;
  wau: number;
  mau: number;
  popularPairs: { pair: string; count: number }[];
  avgCards: number;
  retention7d: number;
  retention30d: number;
}

/**
 * Get user analytics (admin only).
 */
export async function adminGetUserAnalytics(): Promise<UserAnalytics> {
  await requireFreshSession();
  const { data, error } = await supabase.functions.invoke('admin-get-usage', {
    body: { mode: 'users' },
  });
  if (error) throw new Error(error.message);
  return data.users;
}

// ─── App Config API ─────────────────────────────────────────────

/**
 * Read a value from app_config (any authenticated user can read).
 */
export async function adminGetConfig(key: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('app_config')
    .select('value')
    .eq('key', key)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data?.value ?? null;
}

/**
 * Update an app_config value (admin only).
 * Routes through edge function to use service role for writes.
 */
export async function adminUpdateConfig(key: string, value: string): Promise<void> {
  await requireFreshSession();
  const { data, error } = await supabase.functions.invoke('admin-update-config', {
    body: { key, value },
  });
  if (error) throw new Error(error.message);
  if (data?.error) throw new Error(data.error);
}
